import { useCallback, useEffect, useState } from 'react';
import { api, type ApiError } from '../../api/client';
import { Alert } from '../../components/Field';
import { relativeTime } from '../../lib/format';

/**
 * Administration of the course assistant.
 *
 * Two questions, in order, because that is the order an administrator asks
 * them: is the thing working, and does it know anything? A screen that
 * answers only the second leaves "AI unavailable" as the explanation for
 * everything, which nobody can act on — so every component reports its own
 * state and its own sentence, and that sentence names the thing to change.
 */

interface ProviderHealth {
  driver: string;
  configured: boolean;
  reachable: boolean | null;
  model?: string;
  detail: string;
  latencyMs?: number;
}

interface Status {
  feature: {
    enabled: boolean;
    ragEnabled: boolean;
    operational: boolean;
    configurationSource: 'environment' | 'database';
    environment: string;
    reason: string;
  };
  providers: { llm: ProviderHealth; embedding: ProviderHealth };
  vector: { driver: string };
  retrieval: { topK: number; similarityThreshold: number; maxContextTokens: number };
  generation: { temperature: number; maxResponseTokens: number; historyTurns: number };
  limits: { rateLimitPerUserPerHour: number; retentionDays: number };
  knowledge: {
    coursesIndexed: number;
    coursesPublished: number;
    documents: number;
    documentsStale: number;
    documentsFailed: number;
    chunks: number;
    vectors: number;
    chunksWithoutVector: number;
    embedModels: { model: string; dimensions: number; documents: number }[];
  };
  jobs: { queued: number; running: number; done: number; failed: number; latest: unknown };
}

interface CourseRow {
  courseUuid: string;
  courseCode: string;
  courseTitle: string;
  state: string;
  detail: string;
  indexableSources: number;
  indexedDocuments: number;
  staleDocuments: number;
  failedDocuments: number;
  chunks: number;
  lastIndexedAt: string | null;
  embedModels: string[];
  skipped: { sourceLabel: string; location: string; reason: string }[];
}

interface CourseDetail extends CourseRow {
  documents: {
    uuid: string;
    sourceKind: string;
    sourceLabel: string;
    status: string;
    chunkCount: number;
    embedModel: string;
    embedDims: number;
    error: string | null;
    indexedAt: string;
  }[];
}

/** The states, and how each should read at a glance. */
const STATE_TONE: Record<string, string> = {
  INDEXED: 'pill pill--ok',
  STALE: 'pill pill--warn',
  PARTIAL: 'pill pill--warn',
  MODEL_CHANGED: 'pill pill--warn',
  FAILED: 'pill pill--error',
  NOT_INDEXED: 'pill',
  NOTHING_TO_INDEX: 'pill',
};

const STATE_LABEL: Record<string, string> = {
  INDEXED: 'Indexed',
  STALE: 'Re-index required',
  PARTIAL: 'Partly indexed',
  MODEL_CHANGED: 'Model changed',
  FAILED: 'Failed',
  NOT_INDEXED: 'Not indexed',
  NOTHING_TO_INDEX: 'No text to index',
};

export function AiKnowledgeBase() {
  const [status, setStatus] = useState<Status | null>(null);
  const [courses, setCourses] = useState<CourseRow[] | null>(null);
  const [selected, setSelected] = useState<CourseDetail | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [probing, setProbing] = useState(false);

  const load = useCallback(async (probe = false) => {
    setError(null);
    try {
      const [nextStatus, nextCourses] = await Promise.all([
        api<Status>(`/admin/ai/status${probe ? '?probe=true' : ''}`),
        api<CourseRow[]>('/admin/ai/courses'),
      ]);
      setStatus(nextStatus);
      setCourses(nextCourses);
    } catch (caught) {
      setError(caught as ApiError);
    }
  }, []);

  useEffect(() => {
    void load(false);
  }, [load]);

  const act = useCallback(async (course: CourseRow, action: 'index' | 'reindex' | 'delete') => {
    setBusy(`${course.courseUuid}:${action}`);
    setNote(null);
    setError(null);
    try {
      // Written out rather than patched together with a replace: the three
      // actions differ in both path and method, and a string substitution
      // that happens to work is a line nobody can check at a glance.
      const base = `/admin/ai/courses/${course.courseUuid}`;
      const request =
        action === 'delete'
          ? { path: `${base}/index`, method: 'DELETE' }
          : action === 'reindex'
            ? { path: `${base}/reindex`, method: 'POST' }
            : { path: `${base}/index`, method: 'POST' };

      const result = await api<{ message: string }>(request.path, { method: request.method });
      setNote(result.message);
    } catch (caught) {
      setError(caught as ApiError);
    } finally {
      setBusy(null);
    }
  }, []);

  /**
   * Run the queue now.
   *
   * Indexing is normally drained by the housekeeping sweep, which may be an
   * hour away. An administrator who has just clicked Re-index wants to see it
   * happen, so this is offered rather than leaving them to wonder.
   */
  const drain = useCallback(async () => {
    setBusy('drain');
    setNote(null);
    try {
      const result = await api<{
        jobsCompleted: number;
        chunksWritten: number;
        jobsFailed: number;
      }>('/admin/ai/drain', { method: 'POST' });
      setNote(
        `Indexed ${result.jobsCompleted} course(s), ${result.chunksWritten} passages written${
          result.jobsFailed ? `, ${result.jobsFailed} failed` : ''
        }.`,
      );
      await load(false);
    } catch (caught) {
      setError(caught as ApiError);
    } finally {
      setBusy(null);
    }
  }, [load]);

  const open = useCallback(async (course: CourseRow) => {
    setSelected(null);
    try {
      setSelected(await api<CourseDetail>(`/admin/ai/courses/${course.courseUuid}`));
    } catch (caught) {
      setError(caught as ApiError);
    }
  }, []);

  return (
    <div className="stack">
      <header className="page__head aihead">
        <div>
          <h1 className="page__title">AI Assistant</h1>
          <p className="page__lede">
            What Vagdevi knows, how she answers, and whether she is working. Courses are kept up to
            date automatically: a new or edited course reaches her within a few minutes.
          </p>
        </div>
        <div className="row">
          <button
            className="btn btn--outline btn--sm"
            disabled={probing}
            onClick={() => {
              setProbing(true);
              void load(true).finally(() => setProbing(false));
            }}
          >
            {probing ? 'Checking…' : 'Check services'}
          </button>
          <button className="btn btn--sm" disabled={busy === 'drain'} onClick={() => void drain()}>
            {busy === 'drain' ? 'Indexing…' : 'Run indexing now'}
          </button>
        </div>
      </header>

      {error ? <Alert kind="error" title={error.message} items={error.details} /> : null}
      {note ? <Alert kind="success" title={note} /> : null}

      {/* Is it working ------------------------------------------------ */}
      <section className="card">
        <div className="card__head">
          <h2 className="card__title">Service</h2>
          <span className={status?.feature.operational ? 'pill pill--ok' : 'pill pill--warn'}>
            {status?.feature.operational ? 'Available' : 'Not available'}
          </span>
        </div>
        <div className="card__body stack">
          {status ? (
            <>
              <p>{status.feature.reason}</p>

              <div className="grid grid--2">
                <ProviderCard title="Inference" health={status.providers.llm} />
                <ProviderCard title="Embeddings" health={status.providers.embedding} />
              </div>

              <dl className="kv">
                <div>
                  <dt>Vectors stored in</dt>
                  <dd>{status.vector.driver}</dd>
                </div>
                <div>
                  <dt>Passages per question</dt>
                  <dd>{status.retrieval.topK}</dd>
                </div>
                <div>
                  <dt>Confidence threshold</dt>
                  <dd>
                    {status.retrieval.similarityThreshold}
                    <span className="muted"> — below this the assistant declines to answer</span>
                  </dd>
                </div>
                <div>
                  <dt>Questions per learner per hour</dt>
                  <dd>{status.limits.rateLimitPerUserPerHour}</dd>
                </div>
                <div>
                  <dt>Conversations kept for</dt>
                  <dd>{status.limits.retentionDays} days</dd>
                </div>
                <div>
                  <dt>Configuration from</dt>
                  <dd>
                    {status.feature.configurationSource === 'database'
                      ? 'the administration screen'
                      : 'the environment'}
                  </dd>
                </div>
              </dl>
            </>
          ) : (
            <div className="skel" style={{ height: 140 }} />
          )}
        </div>
      </section>

      {/* What it knows ------------------------------------------------ */}
      {status ? (
        <section className="card">
          <div className="card__head">
            <h2 className="card__title">Knowledge</h2>
          </div>
          <div className="card__body">
            <dl className="kv kv--wide">
              <div>
                <dt>Courses indexed</dt>
                <dd>
                  {status.knowledge.coursesIndexed} of {status.knowledge.coursesPublished} published
                </dd>
              </div>
              <div>
                <dt>Passages</dt>
                <dd>{status.knowledge.chunks.toLocaleString()}</dd>
              </div>
              <div>
                <dt>Sources out of date</dt>
                <dd>{status.knowledge.documentsStale}</dd>
              </div>
              <div>
                <dt>Sources failed</dt>
                <dd>{status.knowledge.documentsFailed}</dd>
              </div>
            </dl>

            {/* Two conditions worth surfacing rather than leaving to be
                discovered through answers that quietly miss a passage. */}
            {status.knowledge.chunksWithoutVector > 0 ? (
              <Alert
                kind="warning"
                title={`${status.knowledge.chunksWithoutVector} passages have no embedding and cannot be found. Re-index the affected courses.`}
              />
            ) : null}
            {status.knowledge.embedModels.length > 1 ? (
              <Alert
                kind="warning"
                title={`Content is indexed with more than one embedding model (${status.knowledge.embedModels
                  .map((entry) => `${entry.model} at ${entry.dimensions}`)
                  .join(
                    ', ',
                  )}). Those vectors cannot be compared with each other — re-index everything.`}
              />
            ) : null}
          </div>
        </section>
      ) : null}

      {/* Per course --------------------------------------------------- */}
      <section className="card">
        <div className="card__head">
          <h2 className="card__title">Courses</h2>
          {status ? (
            <span className="muted">
              {status.jobs.queued} queued · {status.jobs.failed} failed
            </span>
          ) : null}
        </div>

        {!courses ? (
          <div className="card__body">
            <div className="skel" style={{ height: 200 }} />
          </div>
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>Course</th>
                <th>State</th>
                <th>Passages</th>
                <th>Last indexed</th>
                <th aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {courses.map((course) => (
                <tr key={course.courseUuid}>
                  <td>
                    <button className="linkbtn" onClick={() => void open(course)}>
                      {course.courseTitle}
                    </button>
                    <div className="muted">{course.courseCode}</div>
                  </td>
                  <td>
                    <span className={STATE_TONE[course.state] ?? 'pill'}>
                      {STATE_LABEL[course.state] ?? course.state}
                    </span>
                    <div className="muted">{course.detail}</div>
                  </td>
                  <td>{course.chunks.toLocaleString()}</td>
                  <td>{course.lastIndexedAt ? relativeTime(course.lastIndexedAt) : '—'}</td>
                  <td className="row-actions">
                    <button
                      className="btn btn--sm btn--outline"
                      disabled={busy === `${course.courseUuid}:index`}
                      onClick={() => void act(course, 'index')}
                    >
                      Index
                    </button>
                    <button
                      className="btn btn--sm btn--outline"
                      disabled={busy === `${course.courseUuid}:reindex`}
                      onClick={() => void act(course, 'reindex')}
                    >
                      Re-index
                    </button>
                    <button
                      className="btn btn--sm btn--quiet"
                      disabled={busy === `${course.courseUuid}:delete` || course.chunks === 0}
                      onClick={() => void act(course, 'delete')}
                    >
                      Forget
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      {/* One course, with what was skipped and why -------------------- */}
      {selected ? (
        <section className="card">
          <div className="card__head">
            <h2 className="card__title">{selected.courseTitle}</h2>
            <button className="btn btn--quiet btn--sm" onClick={() => setSelected(null)}>
              Close
            </button>
          </div>
          <div className="card__body stack">
            <p>{selected.detail}</p>

            <h3 className="h5">Indexed sources ({selected.documents.length})</h3>
            <table className="table">
              <thead>
                <tr>
                  <th>Source</th>
                  <th>Kind</th>
                  <th>Passages</th>
                  <th>State</th>
                </tr>
              </thead>
              <tbody>
                {selected.documents.map((document) => (
                  <tr key={document.uuid}>
                    <td>{document.sourceLabel}</td>
                    <td className="muted">{document.sourceKind}</td>
                    <td>{document.chunkCount}</td>
                    <td>
                      <span className={STATE_TONE[document.status] ?? 'pill'}>
                        {document.status}
                      </span>
                      {document.error ? <div className="muted">{document.error}</div> : null}
                    </td>
                  </tr>
                ))}
                {selected.documents.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="muted">
                      Nothing indexed for this course yet.
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>

            {/*
              Skipped sources are the most useful part of this screen. A course
              that answers badly almost always has a reason sitting here, and
              a silent skip turns that into an unanswerable question.
            */}
            {selected.skipped.length > 0 ? (
              <>
                <h3 className="h5">Not indexed ({selected.skipped.length})</h3>
                <ul className="skiplist">
                  {selected.skipped.map((entry, index) => (
                    <li key={`${entry.sourceLabel}-${index}`}>
                      <strong>{entry.sourceLabel}</strong>
                      <span className="muted"> — {entry.location}</span>
                      <div className="muted">{entry.reason}</div>
                    </li>
                  ))}
                </ul>
              </>
            ) : null}
          </div>
        </section>
      ) : null}
    </div>
  );
}

/**
 * One provider, with its own verdict.
 *
 * "Not configured" and "configured but refusing connections" are different
 * problems with different fixes, and collapsing them into "unavailable" helps
 * nobody — so the two are never merged.
 */
function ProviderCard({ title, health }: { title: string; health: ProviderHealth }) {
  const tone = !health.configured
    ? 'pill'
    : health.reachable === false
      ? 'pill pill--error'
      : health.reachable === true
        ? 'pill pill--ok'
        : 'pill pill--warn';

  const label = !health.configured
    ? 'Not configured'
    : health.reachable === false
      ? 'Not answering'
      : health.reachable === true
        ? 'Answering'
        : 'Not checked';

  return (
    <div className="minicard">
      <div className="minicard__head">
        <h3 className="h5">{title}</h3>
        <span className={tone}>{label}</span>
      </div>
      <dl className="kv">
        <div>
          <dt>Driver</dt>
          <dd>{health.driver}</dd>
        </div>
        {health.model ? (
          <div>
            <dt>Model</dt>
            <dd>{health.model}</dd>
          </div>
        ) : null}
        {health.latencyMs !== undefined ? (
          <div>
            <dt>Responded in</dt>
            <dd>{health.latencyMs} ms</dd>
          </div>
        ) : null}
      </dl>
      <p className="muted">{health.detail}</p>
    </div>
  );
}
